"use client"

import Image from "next/image"
import { Typography } from "@/shared/ui/atoms"
import { CustomButton } from "@/shared/ui/molecules"
import type { PartnerHospitalDoctor } from "@/entities/hospital/api/types"
import { getDoctorPositionLabel } from "@/widgets/hospital/lib/doctorPosition"
import { hasDoctorCareers } from "@/widgets/hospital/lib/doctorCareers"
import { useState } from "react"
import { useTranslations } from "next-intl"

interface DoctorDetailTemplateProps {
  doctor: PartnerHospitalDoctor
}

const DoctorDetailTemplate = ({ doctor }: DoctorDetailTemplateProps) => {
  const t = useTranslations()
  const careerList = doctor.careerDescription || []
  const [isMoreCareer, setIsMoreCareer] = useState(false)
  const [isViewCareerList, setIsViewCareerList] = useState<string[]>(careerList.slice(0, 5))
  const checkedCareerList = careerList && careerList.length > 0

  const positionLabel = getDoctorPositionLabel(doctor.positionCode)
  const checkedMajorTreatments = doctor.majorTreatments && doctor.majorTreatments.length > 0

  const handleMoreCareer = () => {
    setIsMoreCareer((prev) => {
      const value = !prev
      const newList = value ? careerList : careerList.slice(0, 5)
      setIsViewCareerList(newList)
      return value
    })
  }

  return (
    <div className="flex-1 flex flex-col bg-white border-t border-gray-100">
      <div className="mx-4 pt-[10px] pb-[14px] border-b border-gray-100">
        <div className="flex items-center gap-[14px]">
          <div className="relative w-[87px] h-[87px] overflow-hidden rounded-full bg-gray-200">
            <Image src={doctor.profileImageUrl} alt={doctor.name} fill sizes="87px" className="object-cover" priority />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-1">
          <Typography size={24} lineHeight={28} className="text-gray-900 font-bold">
            {doctor.name}
          </Typography>
          {positionLabel && (
            <Typography size={24} lineHeight={28} className="text-gray-900 font-bold">
              {positionLabel}
            </Typography>
          )}
        </div>

        <div className="mt-2 flex items-center gap-2">
          {doctor.specialtyKeywords && (
            <div className="flex items-center gap-1">
              <Image src="/icons/medal.png" alt="medal" width={16} height={16} />
              <Typography size={14} lineHeight={18} className="text-gray-700 font-medium">
                {doctor.specialtyKeywords}
              </Typography>
            </div>
          )}
          {hasDoctorCareers(doctor.careers) && (
            <div className="flex items-center gap-1">
              <Image src="/icons/check-mark.png" alt="career" width={16} height={16} />
              <Typography size={14} lineHeight={18} className="text-gray-700 font-medium">
                {t("doctorDetail.careersYears", { years: doctor.careers })}
              </Typography>
            </div>
          )}
        </div>
      </div>

      { checkedMajorTreatments && <div className="mt-3 pb-5 px-4">
        <Typography size={16} className="pb-2 text-gray-800 font-semibold">
          {t("doctorDetail.majorTreatments")}
        </Typography>
        <div className="flex flex-wrap gap-2">
          {doctor.majorTreatments.map((treatment) => (
            <Typography
              key={treatment}
              size={14}
              lineHeight={18}
              className="px-3 py-2 rounded-full bg-gray-100 text-gray-700 font-medium"
            >
              {treatment}
            </Typography>
          ))}
        </div>
      </div>}

      {checkedCareerList && <div className="pt-1 pb-2">
        <Typography size={16} className="px-4 py-2 text-gray-800 font-semibold">
          {t("doctorDetail.career")}
        </Typography>
        <div className="px-5">
          <ul
            className={`list-disc pl-5 text-gray-800 marker:text-[10px] marker:text-gray-800`}
          >
            {isViewCareerList?.map((career, idx) => (
              <li key={`${idx}-${career}`}>
                <Typography size={15} lineHeight={22} className="text-gray-800">
                  {career}
                </Typography>
              </li>
            ))}
          </ul>

          {checkedCareerList && careerList.length > 5 && (
            <CustomButton
              className="mt-5 w-full h-[34px] bg-gray-100 rounded-lg"
              onClick={handleMoreCareer}
            >
              <Typography size={14} lineHeight={20} className="text-gray-900 font-medium">
                {isMoreCareer ? t("common.collapse") : t("common.more")}
              </Typography>
            </CustomButton>
          )}
        </div>
      </div>}
    </div>
  )
}

export default DoctorDetailTemplate

